import { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getALlowedSections, getUserPermissions } from "../../controllers/user";
import { clearCache, setCache } from "../../controllers/cache";

const AuthContext = createContext({
  token: '',
  allowedSections: [],
  onLogin: () => {},
  onLogout: () => {},
  onCompleteRegistration: () => {},
});

const useAuth = () => {
  return useContext(AuthContext);
};

const decodeJwt = (token) => {
  return JSON.parse(atob(token.split(".")[1]));
}

const AuthProvider = ({ children }) => {
  const [token, setToken] = useState('');
  const [permissions, setPermissions] = useState([]);
  const [isTokenExpired, setIsTokenExpired] = useState(false);
  const [allowedSections, setAllowedSections] = useState([]);
  const navigate = useNavigate();

  if (!token) {
    let t = localStorage.getItem('token');
    if (t) {
      const decodedJwt = JSON.parse(atob(t.split(".")[1]));
      if(decodedJwt.exp * 1000 < Date.now()) {
        setIsTokenExpired(true);
        localStorage.removeItem('token');
        localStorage.removeItem('sections');
        localStorage.removeItem('permissions');
        clearCache('token');
      }
      else {
        setPermissions(decodedJwt.authInfo.userPermissions);
        setToken(t);
        setCache('token', t, (decodedJwt.exp * 1000) - Date.now());
      }
    }
  }
  
  if (!allowedSections) {
    let s = JSON.parse(localStorage.getItem('sections'));
    if (s) {
      setAllowedSections(s);
    }
  }
  else if (allowedSections && !allowedSections.length) {
    let s = JSON.parse(localStorage.getItem('sections'));
    if (s && JSON.stringify(s) !== JSON.stringify(allowedSections)) {
      setAllowedSections(s);
    }
  }

  useEffect(() => {
    localStorage.setItem('token', token);
    setCache('token', token, 60 * 60 * 1000);
  }, [token]);
  useEffect(() => {
    console.log('Allowed sections: ', allowedSections);
    localStorage.setItem('sections', JSON.stringify(allowedSections));
  }, [allowedSections]);
  useEffect(() => {
    console.log('permissions: ', permissions);
    localStorage.setItem('permissions', JSON.stringify(permissions));
  }, [permissions]);
  useEffect(() => {
    const refreshPermissions = async () => {
      if (!token) {
        return;
      }

      try {
        const data = await getUserPermissions(token);
        if (Array.isArray(data.permissions)) {
          setPermissions(data.permissions);
        }
      }
      catch (e) {
        console.log('Could not refresh permissions', e);
      }
    };

    refreshPermissions();
  }, [token]);

  const handleLogin = async (data) => {
    if (!data?.token) {
      return;
    }

    setToken(data.token);
    const decodedToken = decodeJwt(data.token);
    localStorage.setItem('token', data.token);
    setCache('token', data.token, (decodedToken.exp * 1000) - Date.now());
    setPermissions(decodedToken.authInfo.userPermissions);

    const nextSections = Array.isArray(data.allowedSections) ? data.allowedSections : [];
    setAllowedSections(nextSections);

    if (data.membership && data.isMembershipComplete && !data.registration) {
      navigate('/app');
    }
    else if (data.membership && !data.isMembershipComplete) {
      console.log('membership', data.membership, data.isMembershipComplete);
      navigate('/registro', { state: {membership: data.membership, confirmNewMembership: data.confirmNewMembership }} );
    }
    else if (data.pendingRegistration || data.user || (Object.prototype.hasOwnProperty.call(data, 'isMembershipComplete') && !data.membership && !data.isMembershipComplete)) {
      navigate('/registro', { state: { pendingRegistration: data.pendingRegistration, user: data.user } });
    }
    else {
      navigate('/registro');
    }
  };

  const handleCompleteRegistration = async (newToken) => {
    setToken(newToken);
    const decodedToken = decodeJwt(newToken);
    localStorage.setItem('token', newToken);
    setCache('token', newToken, (decodedToken.exp * 1000) - Date.now());
    setPermissions(decodedToken.authInfo.userPermissions);
    const sections = await getALlowedSections(newToken);
    setAllowedSections(sections);
  };

  const handleLogout = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    localStorage.removeItem('token');
    localStorage.removeItem('sections');
    localStorage.removeItem('permissions');
    setToken('');
    setPermissions();
    setAllowedSections([]);
    navigate('/');
  };

  const hasPermission = (permissionKey) => {
    return Object.values(permissions).includes(permissionKey);
  }

  const value = {
    token,
    allowedSections: allowedSections,
    permissions: permissions,
    onLogin: handleLogin,
    onLogout: handleLogout,
    onCompleteRegistration: handleCompleteRegistration,
    hasPermission: hasPermission
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export {
  AuthProvider,
  AuthContext,
  useAuth
};
